import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  ILAN_ID: "",
  HIZMET_ADI: "",
  UCRETSIZ_MI: true,
  EK_UCRET: "",
};

const IsletmeHizmetleri = () => {
  document.title = "İşletme Hizmetleri | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${BASE_URL}/ISLETME_HIZMETLERI?$orderby=ID desc&$expand=ILANLAR($select=AD)`,
        { headers }
      );
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("İşletme hizmetleri yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      ID: row.ID,
      ILAN_ID: row.ILAN_ID ?? "",
      HIZMET_ADI: row.HIZMET_ADI || "",
      UCRETSIZ_MI: row.UCRETSIZ_MI !== false,
      EK_UCRET: row.EK_UCRET ?? "",
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.HIZMET_ADI.trim()) {
      setError("Hizmet adı zorunludur.");
      return;
    }
    if (formData.ILAN_ID === "" || isNaN(Number(formData.ILAN_ID))) {
      setError("Geçerli bir ilan ID giriniz.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ILAN_ID: Number(formData.ILAN_ID),
        HIZMET_ADI: formData.HIZMET_ADI.trim(),
        UCRETSIZ_MI: formData.UCRETSIZ_MI,
        EK_UCRET: formData.UCRETSIZ_MI || formData.EK_UCRET === "" ? null : Number(formData.EK_UCRET),
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/ISLETME_HIZMETLERI(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Hizmet güncellendi.");
      } else {
        await fetch(`${BASE_URL}/ISLETME_HIZMETLERI`, {
          method: "POST", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Hizmet eklendi.");
      }

      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu hizmeti silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/ISLETME_HIZMETLERI(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Hizmet silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "İlan",
      accessorKey: "ILAN_ID",
      enableSorting: true,
      cell: (c) => {
        const row = c.row.original;
        return (
          <div>
            <div className="fw-medium">{row.ILANLAR?.AD || "—"}</div>
            <small className="text-muted">İlan #{c.getValue()}</small>
          </div>
        );
      },
    },
    {
      header: "Hizmet Adı",
      accessorKey: "HIZMET_ADI",
      enableSorting: true,
      cell: (c) => <span className="fw-medium">{c.getValue() || "---"}</span>,
    },
    {
      header: "Ücret",
      accessorKey: "UCRETSIZ_MI",
      enableSorting: false,
      cell: (c) => {
        const row = c.row.original;
        return c.getValue()
          ? <Badge color="success">Ücretsiz</Badge>
          : <Badge color="warning" className="text-dark">{row.EK_UCRET != null ? `${row.EK_UCRET} ₺` : "Ek ücretli"}</Badge>;
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(row)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İlan Yönetimi" breadcrumbItem="İşletme Hizmetleri" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">İşletme / İlan Hizmetleri</h4>
                      <p className="text-muted small mb-0">
                        İlanlara bağlı ek hizmetleri (ücretsiz / ek ücretli) buradan yönetin.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Hizmet
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Hizmet ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-room-service-outline" : "mdi-plus-box-outline"} me-2 text-primary`}></i>
          {isEditing ? `Hizmet Düzenle (#${formData.ID})` : "Yeni Hizmet Ekle"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">İlan ID <span className="text-danger">*</span></Label>
                <Input
                  type="number"
                  value={formData.ILAN_ID}
                  onChange={(e) => f("ILAN_ID", e.target.value)}
                  placeholder="Örn: 340"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Hizmet Adı <span className="text-danger">*</span></Label>
                <Input
                  value={formData.HIZMET_ADI}
                  onChange={(e) => f("HIZMET_ADI", e.target.value)}
                  placeholder="Örn: Kesim hizmeti"
                  maxLength={200}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup check className="mt-4">
                <Input
                  type="checkbox"
                  id="ucretsizMi"
                  checked={formData.UCRETSIZ_MI}
                  onChange={(e) => f("UCRETSIZ_MI", e.target.checked)}
                />
                <Label check for="ucretsizMi" className="fw-bold ms-1">
                  Ücretsiz hizmet
                </Label>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Ek Ücret (₺)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.EK_UCRET}
                  onChange={(e) => f("EK_UCRET", e.target.value)}
                  placeholder="0.00"
                  disabled={formData.UCRETSIZ_MI}
                />
                <small className="text-muted">Ücretsiz işaretliyse dikkate alınmaz.</small>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default IsletmeHizmetleri;
